import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  AvatarGroup,
  Tooltip,
  CircularProgress,
  IconButton,
  Badge
} from '@mui/material';
import {
  FiberManualRecord as OnlineIcon,
  People as PeopleIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon
} from '@mui/icons-material';
import OnlineUsers from './OnlineUsers';
import useAuthStore from '../../stores/authStore';
import { useSocket } from '../../contexts/SocketContext';

const OnlineUsersWidget = ({ collapsed = false }) => {
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const isSuperAdmin = useAuthStore(state => state.isSuperAdmin);
  const { socket, connected } = useSocket();

  useEffect(() => {
    // Only proceed if user is super admin and socket is connected
    if (!isSuperAdmin() || !connected || !socket) {
      setLoading(false);
      return;
    }

    // Request initial online users list
    socket.emit('get:online-users');

    // Listen for initial users list
    const handleOnlineUsersList = (data) => {
      setOnlineUsers(data.users || []);
      setLoading(false);
    };

    // Listen for real-time user online/offline events
    const handleUserOnline = (data) => {
      setOnlineUsers(prev => {
        // Check if user already exists
        const existingIndex = prev.findIndex(user => user._id === data.user._id);
        if (existingIndex >= 0) {
          // Update existing user
          const updated = [...prev];
          updated[existingIndex] = { ...updated[existingIndex], ...data.user };
          return updated;
        } else {
          // Add new user
          return [...prev, data.user];
        }
      });
    };

    const handleUserOffline = (data) => {
      setOnlineUsers(prev => prev.filter(user => user._id !== data.userId));
    };

    // Set up event listeners
    socket.on('online-users:list', handleOnlineUsersList);
    socket.on('user:online', handleUserOnline);
    socket.on('user:offline', handleUserOffline);

    // Cleanup event listeners
    return () => {
      socket.off('online-users:list', handleOnlineUsersList);
      socket.off('user:online', handleUserOnline);
      socket.off('user:offline', handleUserOffline);
    };
  }, [isSuperAdmin, connected, socket]);

  const getAvatarText = (name) => {
    if (!name) return '?';
    const names = name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  if (collapsed) {
    return (
      <Tooltip title={`${onlineUsers.length} users online`}>
        <IconButton sx={{ position: 'relative' }}>
          <Badge
            badgeContent={onlineUsers.length}
            color="success"
            max={99}
          >
            <PeopleIcon />
          </Badge>
        </IconButton>
      </Tooltip>
    );
  }

  if (expanded) {
    return (
      <Box>
        <Paper 
          sx={{ 
            p: 2, 
            mb: 2, 
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' }
          }}
          onClick={() => setExpanded(false)}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              <OnlineIcon sx={{ color: 'success.main', fontSize: 14 }} />
              <Typography variant="subtitle2">
                Online Users ({onlineUsers.length})
              </Typography>
            </Box>
            <CollapseIcon fontSize="small" />
          </Box>
        </Paper>
        <OnlineUsers compact maxHeight="300px" />
      </Box>
    );
  }

  return (
    <Paper 
      sx={{ 
        p: 2, 
        cursor: 'pointer',
        '&:hover': { bgcolor: 'action.hover' }
      }}
      onClick={() => setExpanded(true)}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Box display="flex" alignItems="center" gap={1}>
          <OnlineIcon sx={{ color: 'success.main', fontSize: 14 }} />
          <Typography variant="subtitle2">
            Online Users ({onlineUsers.length})
          </Typography>
        </Box>
        <ExpandIcon fontSize="small" />
      </Box>
      
      {loading ? (
        <Box display="flex" justifyContent="center" py={1}>
          <CircularProgress size={20} />
        </Box>
      ) : onlineUsers.length > 0 ? (
        <AvatarGroup 
          max={6} 
          sx={{ 
            justifyContent: 'flex-start',
            '& .MuiAvatar-root': {
              width: 32,
              height: 32,
              fontSize: '0.875rem',
              bgcolor: 'primary.main'
            }
          }}
        >
          {onlineUsers.map((user) => (
            <Tooltip key={user._id} title={`${user.name} - ${user.role || 'User'}`}>
              <Avatar src={user.avatar} alt={user.name}>
                {getAvatarText(user.name)}
              </Avatar>
            </Tooltip>
          ))}
        </AvatarGroup>
      ) : (
        <Typography variant="caption" color="text.secondary">
          No users online
        </Typography>
      )}
    </Paper>
  );
};

export default OnlineUsersWidget;